import { useTranslation } from "react-i18next";
import type { Integrator } from "../../../../../../store/reseller-admin/integratorsStore";
import type { ThemeClasses } from "../types";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface IntegratorGridCardProps {
  integrator: Integrator;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (integrator: Integrator) => void;
  onEdit: (integrator: Integrator) => void;
  onDelete: (integrator: Integrator) => void;
  editLoading?: boolean;
}

export default function IntegratorGridCard({
  integrator,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  editLoading = false,
}: IntegratorGridCardProps) {
  const { t } = useTranslation("resellerAdmin");
  const { isUserDev } = useIsUserDev();

  const protocolLabel = integrator.protocol
    ? t(`integrators.options.protocol.${integrator.protocol}`, {
        defaultValue: integrator.protocol,
      })
    : t("integrators.noData");
  const adapterLabel = integrator.adapter
    ? t(`integrators.options.adapter.${integrator.adapter}`, {
        defaultValue: integrator.adapter,
      })
    : t("integrators.noData");
  const credentialsCount = integrator.credentials_schema?.length || 0;

  return (
    <div
      className={`rounded-2xl border ${themeClasses.cardBorder} ${themeClasses.cardBg} p-5 flex flex-col gap-3 transition-all hover:shadow-lg`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isDarkMode ? "bg-primary-500/15" : "bg-primary-50"
            }`}
          >
            <i className="ri-plug-line text-lg text-primary-500"></i>
          </div>
          <div className="min-w-0">
            <h3 className={`font-semibold truncate ${themeClasses.text}`}>
              {integrator.name}
            </h3>
            <span
              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full mt-1 ${
                integrator.active
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              }`}
            >
              <i
                className={`${
                  integrator.active ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"
                }`}
              ></i>
              {integrator.active
                ? t("integrators.active")
                : t("integrators.inactive")}
            </span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("integrators.fields.protocol")}
          </p>
          <p className={`${themeClasses.text} uppercase`}>{protocolLabel}</p>
        </div>
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("integrators.fields.adapter")}
          </p>
          <p className={`${themeClasses.text} truncate`}>{adapterLabel}</p>
        </div>
        <div className="col-span-2">
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("integrators.fields.defaultEndpoint")}
          </p>
          <p className={`${themeClasses.text} truncate`}>
            {integrator.default_endpoint || t("integrators.noData")}
          </p>
        </div>
      </div>

      <p className={`text-xs ${themeClasses.textSecondary}`}>
        <i className="ri-key-2-line mr-1"></i>
        {t("integrators.credentialsCount", { count: credentialsCount })}
      </p>

      {/* Actions */}
      <div className={`flex items-center gap-2 pt-3 border-t ${themeClasses.border}`}>
        <button
          onClick={() => onDetails(integrator)}
          className={`flex-1 text-sm py-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} transition-all`}
        >
          <i className="ri-eye-line mr-1"></i>
          {t("integrators.actions.details")}
        </button>
        {isUserDev && (
          <>
            <button
              onClick={() => onEdit(integrator)}
              disabled={editLoading}
              className="flex-1 text-sm py-2 rounded-lg bg-primary-500/10 text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-all disabled:opacity-60 disabled:cursor-wait inline-flex items-center justify-center"
            >
              {editLoading ? (
                <i className="ri-loader-4-line animate-spin mr-1"></i>
              ) : (
                <i className="ri-edit-line mr-1"></i>
              )}
              {t("integrators.actions.edit")}
            </button>
            <button
              onClick={() => onDelete(integrator)}
              className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
